import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { normalisePhone } from "@mhj/shared/phone";
import { useAuth } from "@mhj/shared/auth";
import DocumentUpload from "../components/DocumentUpload";
import { Card, Field, buttonClass, inputClass, secondaryButtonClass } from "../lib/ui";
import { docLabel } from "../lib/docs";
import type { EmpowermentConfig } from "../lib/types";

type Capture = {
  applicationId: string;
  userId: string;
  claimCode: string;
};

type OfficerRow = {
  id: string;
  applicationNo: string | null;
  userName: string;
  userPhone: string | null;
  trackName: string | null;
  ward: string;
  status: string;
  uploadedDocs: string[];
};

type Lookup = {
  found: boolean;
  phone: string;
  user?: { id: string; fullName: string };
  alreadyAppliedThisCycle?: boolean;
  history?: { applicationNo: string | null; status: string; cycleName: string | null; trackName: string | null }[];
};

/**
 * Capture form for a field officer at a ward meeting.
 *
 * One screen, no steps: the officer is standing up, holding a phone, with
 * someone waiting. Everything the self-service flow spreads over four steps is
 * here in one scroll.
 */
export default function OfficerCapture() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const [capture, setCapture] = useState<Capture | null>(null);
  const [form, setForm] = useState({
    fullName: "", phone: "", email: "", gender: "", dateOfBirth: "", nin: "",
    ward: "", address: "", educationLevel: "", employmentStatus: "",
    maritalStatus: "", dependants: 0, incomeBand: "", trackId: "",
    trackDetail: "", bankName: "", bankAccountNumber: "", bankAccountName: "",
  });

  const { data: config } = useQuery({
    queryKey: ["empowerment", "config"],
    queryFn: () => apiRequest<EmpowermentConfig>("/empowerment/config"),
  });

  const { data: mine } = useQuery({
    queryKey: ["empowerment", "officer", "applications"],
    queryFn: () =>
      apiRequest<{ applications: OfficerRow[] }>("/empowerment/officer/applications"),
  });

  const track = (config?.tracks ?? []).find((t) => t.id === form.trackId) ?? null;

  const checkPhone = useMutation({
    mutationFn: (phone: string) =>
      apiRequest<Lookup>("/empowerment/officer/lookup", { params: { phone } }),
    onSuccess: (result) => setLookup(result),
    onError: (e: Error) => toast.error(e.message),
  });

  const submitCapture = useMutation({
    mutationFn: () =>
      apiRequest<Capture>("/empowerment/officer/capture", {
        method: "POST",
        withCsrf: true,
        data: {
          fullName: form.fullName.trim(),
          phone: normalisePhone(form.phone),
          email: form.email.trim() || null,
          trackId: form.trackId,
          gender: form.gender,
          dateOfBirth: form.dateOfBirth,
          nin: form.nin.trim() || null,
          ward: form.ward,
          address: form.address.trim() || null,
          educationLevel: form.educationLevel,
          employmentStatus: form.employmentStatus,
          maritalStatus: form.maritalStatus,
          dependants: Number(form.dependants),
          incomeBand: form.incomeBand,
          trackDetail: form.trackDetail.trim() || null,
          bankName: form.bankName.trim() || null,
          bankAccountNumber: form.bankAccountNumber.trim() || null,
          bankAccountName: form.bankAccountName.trim() || null,
        },
      }),
    onSuccess: (result) => {
      setCapture(result);
      void queryClient.invalidateQueries({ queryKey: ["empowerment", "officer"] });
      toast.success("Applicant captured. Now add their documents.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const finish = useMutation({
    mutationFn: (applicationId: string) =>
      apiRequest<{ applicationNo: string }>(
        `/empowerment/officer/applications/${applicationId}/submit`,
        { method: "POST", withCsrf: true, data: {} },
      ),
    onSuccess: (result) => {
      toast.success(`Submitted. ID ${result.applicationNo}`);
      setCapture(null);
      setLookup(null);
      setForm({
        fullName: "", phone: "", email: "", gender: "", dateOfBirth: "", nin: "",
        ward: "", address: "", educationLevel: "", employmentStatus: "",
        maritalStatus: "", dependants: 0, incomeBand: "", trackId: "",
        trackDetail: "", bankName: "", bankAccountNumber: "", bankAccountName: "",
      });
      void queryClient.invalidateQueries({ queryKey: ["empowerment", "officer"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (user && user.role !== "field_officer" && user.role !== "super_admin") {
    return (
      <Card title="Not available">
        <p className="text-slate-600">This screen is for field officers.</p>
      </Card>
    );
  }

  if (!config?.cycle) {
    return (
      <Card title="Applications are closed">
        <p className="text-slate-600">{config?.message ?? "The next round opens soon."}</p>
      </Card>
    );
  }

  const captured = mine?.applications ?? [];
  const current = capture
    ? captured.find((a) => a.id === capture.applicationId) ?? null
    : null;
  const missingDocs = track
    ? track.requiredDocs.filter((d) => !(current?.uploadedDocs ?? []).includes(d))
    : [];

  return (
    <div className="space-y-5">
      <Card
        title="Capture an applicant"
        description="For someone applying in person who cannot use the website themselves."
      >
        {!capture && (
          <div className="space-y-4">
            <Field
              label="Their phone number"
              required
              hint="Check this first. One number can only hold one account."
            >
              <div className="flex gap-2">
                <input
                  className={inputClass}
                  value={form.phone}
                  onChange={(e) => { setForm({ ...form, phone: e.target.value }); setLookup(null); }}
                  type="tel"
                  inputMode="tel"
                  placeholder="08031234567"
                />
                <button
                  type="button"
                  onClick={() => checkPhone.mutate(form.phone)}
                  disabled={checkPhone.isPending || !form.phone}
                  className="shrink-0 rounded-2xl border border-slate-300 px-4 text-sm font-medium text-slate-700 disabled:opacity-50"
                >
                  {checkPhone.isPending ? "…" : "Check"}
                </button>
              </div>
            </Field>

            {lookup?.found && (
              <div className="rounded-2xl bg-amber-50 p-4 text-sm">
                <p className="font-semibold text-amber-900">
                  This number belongs to {lookup.user?.fullName}
                </p>
                {lookup.alreadyAppliedThisCycle ? (
                  <p className="mt-1 text-amber-800">
                    They have already applied in this round. Nothing more to do.
                  </p>
                ) : (
                  <p className="mt-1 text-amber-800">
                    If this is the same person, they can apply on their own account.
                    If it is someone else, take a different phone number or an email
                    address for them — one number cannot hold two accounts.
                  </p>
                )}
                {(lookup.history ?? []).length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
                    {lookup.history!.map((h, i) => (
                      <li key={i}>{h.cycleName} — {h.trackName} — {h.status}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {lookup && !lookup.found && (
              <p className="rounded-2xl bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
                That number is free. Fill in the rest below.
              </p>
            )}

            <Field label="Their full name" required>
              <input
                className={inputClass}
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Gender" required>
                <select className={inputClass} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                  <option value="">Select…</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </Field>
              <Field label="Date of birth" required>
                <input type="date" className={inputClass} value={form.dateOfBirth}
                  onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
              </Field>
              <Field label="Ward" required>
                <select className={inputClass} value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })}>
                  <option value="">Select…</option>
                  {config.wards.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </Field>
              <Field label="NIN">
                <input className={inputClass} value={form.nin} inputMode="numeric"
                  onChange={(e) => setForm({ ...form, nin: e.target.value.replace(/\D/g, "").slice(0, 11) })} />
              </Field>
              <Field label="Education" required>
                <select className={inputClass} value={form.educationLevel} onChange={(e) => setForm({ ...form, educationLevel: e.target.value })}>
                  <option value="">Select…</option>
                  {["none", "primary", "secondary", "vocational", "tertiary"].map((v) => (
                    <option key={v} value={v}>{v.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </Field>
              <Field label="Occupation" required>
                <select className={inputClass} value={form.employmentStatus} onChange={(e) => setForm({ ...form, employmentStatus: e.target.value })}>
                  <option value="">Select…</option>
                  {["unemployed", "self_employed", "employed", "student", "farmer"].map((v) => (
                    <option key={v} value={v}>{v.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </Field>
              <Field label="Marital status" required>
                <select className={inputClass} value={form.maritalStatus} onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })}>
                  <option value="">Select…</option>
                  {["single", "married", "widowed", "divorced"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Income" required>
                <select className={inputClass} value={form.incomeBand} onChange={(e) => setForm({ ...form, incomeBand: e.target.value })}>
                  <option value="">Select…</option>
                  {["none", "under_30k", "30k_100k", "100k_plus"].map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Dependants">
                <input type="number" min={0} className={inputClass} value={form.dependants}
                  onChange={(e) => setForm({ ...form, dependants: Number(e.target.value) })} />
              </Field>
            </div>

            <Field label="What are they applying for?" required>
              <select className={inputClass} value={form.trackId} onChange={(e) => setForm({ ...form, trackId: e.target.value })}>
                <option value="">Select…</option>
                {config.tracks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>

            {track && (
              <Field label={track.detailPrompt} required={track.detailRequired}>
                <textarea className={`${inputClass} min-h-20`} value={form.trackDetail}
                  onChange={(e) => setForm({ ...form, trackDetail: e.target.value })} />
              </Field>
            )}

            {track?.requiresBankDetails && (
              <div className="space-y-3 rounded-2xl bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">
                  This track can award money, so bank details are needed.
                </p>
                <Field label="Bank" required>
                  <input className={inputClass} value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
                </Field>
                <Field label="Account number" required>
                  <input className={inputClass} inputMode="numeric" value={form.bankAccountNumber}
                    onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                </Field>
                <Field label="Account name" required>
                  <input className={inputClass} value={form.bankAccountName} onChange={(e) => setForm({ ...form, bankAccountName: e.target.value })} />
                </Field>
              </div>
            )}

            <button
              type="button"
              onClick={() => submitCapture.mutate()}
              disabled={submitCapture.isPending || lookup?.found === true}
              className={buttonClass}
            >
              {submitCapture.isPending ? "Saving…" : "Save and add documents"}
            </button>
          </div>
        )}

        {capture && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-50 p-4">
              <p className="font-semibold text-emerald-900">{form.fullName} captured</p>
              <p className="mt-1 text-sm text-emerald-800">
                Their claim code is <strong className="font-mono text-base">{capture.claimCode}</strong>.
                They will also get it by text message when you finish.
              </p>
            </div>

            <div className="space-y-3">
              {(track?.requiredDocs ?? []).map((docType) => (
                <DocumentUpload
                  key={docType}
                  applicationId={capture.applicationId}
                  docType={docType}
                  label={docLabel(docType)}
                  uploaded={(current?.uploadedDocs ?? []).includes(docType)}
                />
              ))}
            </div>

            {missingDocs.length > 0 && (
              <p className="text-sm text-amber-800">
                Still needed: {missingDocs.map(docLabel).join(", ")}
              </p>
            )}

            <button
              type="button"
              onClick={() => finish.mutate(capture.applicationId)}
              disabled={finish.isPending || missingDocs.length > 0}
              className={buttonClass}
            >
              {finish.isPending ? "Submitting…" : "Finish and submit"}
            </button>
            <button type="button" onClick={() => setCapture(null)} className={secondaryButtonClass}>
              Leave for now
            </button>
          </div>
        )}
      </Card>

      {captured.length > 0 && (
        <Card title={`Your captures (${captured.length})`}>
          <div className="space-y-2">
            {captured.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{row.userName}</p>
                  <p className="text-xs text-slate-500">
                    {row.applicationNo ?? "Not submitted"} · {row.trackName} · {row.ward}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  row.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"
                }`}>
                  {row.status === "draft" ? "Unfinished" : row.status.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
